import React, { useEffect, useState } from "react";
import { ChevronRight, Calendar, ChevronLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import textTranslator from "../../../utils/useTextTranslator";
import typeTranslator from "../../../utils/useTypeTranslator";
import { arClass } from "../../../utils/useArClass";
import Txt from "../../../components/Text";
import formatDate from "../../../utils/formatDate";
import formatTime from "../../../utils/formatTime";
import {
  apiFetchError,
  apiRequest,
  apiSuccess,
} from "../../../redux/api/apiActions";
import api from "../../../api";

/**
 * Text Component Wrapper for Waiting Room Page
 * Props:
 * - children: Text content
 * - className: Additional CSS classes
 * @returns Text JSX
 */

const Text = ({ children, className, page = "APPOINTMENTS" }) => {
  return (
    <Txt page={page} className={className}>
      {children}
    </Txt>
  );
};

// Placeholder component for UpcomingAppointments
const UpcomingAppointmentsPlaceholder = ({ darkMode = false }) => {
  return (
    <div
      className={arClass(`p-5 sm:rounded-2xl sm:shadow-[0_1px_4px_rgba(0,0,0,0.06)] sm:border sm:border-slate-100 overflow-hidden 
                    bg-transparent shadow-none border-0
                    ${darkMode ? "sm:bg-gray-800 sm:border-gray-700" : "sm:bg-white sm:border-slate-100"}`)}
    >
      {/* Title Placeholder with shimmer */}
      <div className={arClass("relative overflow-hidden mb-3.5")}>
        <div
          className={arClass(
            `h-5 w-40 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`,
          )}
        />
        <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>

      {/* Appointment Items Placeholder */}
      <div className={arClass("flex flex-col")}>
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className={arClass(
              `relative overflow-hidden flex items-center justify-between py-2.5 ${i < 2 ? `border-b ${darkMode ? "border-gray-700" : "border-slate-100"}` : ""}`,
            )}
          >
            <div className={arClass("flex items-center gap-2.5 flex-1")}>
              {/* Calendar Icon Placeholder */}
              <div
                className={arClass(
                  `w-11 h-12 rounded-lg ${darkMode ? "bg-gray-700" : "bg-gray-200"} flex-shrink-0`,
                )}
              />

              <div className={arClass("flex-1")}>
                {/* Appointment Type Placeholder */}
                <div
                  className={arClass(
                    `h-4 w-28 rounded mb-1 ${darkMode ? "bg-gray-700" : "bg-gray-200"}`,
                  )}
                />
                {/* Time Placeholder */}
                <div
                  className={arClass(
                    `h-3 w-20 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`,
                  )}
                />
              </div>
            </div>

            <div
              className={arClass(
                "flex items-center gap-1.5 flex-shrink-0 ml-4",
              )}
            >
              {/* Status Badge Placeholder */}
              <div
                className={arClass(
                  `h-5 w-16 rounded-lg ${darkMode ? "bg-gray-700" : "bg-gray-200"}`,
                )}
              />
              {/* Chevron icon placeholder */}
              <div
                className={arClass(
                  `w-3.5 h-3.5 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`,
                )}
              />
            </div>

            {/* Shimmer overlay */}
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>
        ))}
      </div>

      {/* View All Button Placeholder */}
      <div className={arClass("relative overflow-hidden mt-3.5")}>
        <div
          className={arClass(
            `h-4 w-36 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`,
          )}
        />
        <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>
    </div>
  );
};

const UpcomingAppointments = () => {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );
  const { requests } = useSelector((state) => state.api);
  const { id } = useParams();
  const patientKey = `getPatient_${id}`;
  const appointmentsKey = `getAppointmentsByPatient_${id}`;
  const patientLoading = requests[patientKey]?.loading;
  const appointmentsLoading = requests[appointmentsKey]?.loading;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [appointments, setAppointments] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        dispatch(apiRequest("GET", appointmentsKey));
        const response = await api.get(
          `/appointments/patient-appointments/${id}/?includeCreator=true&pageSize=5`,
        );

        setAppointments(response.data.results || response.data);
        dispatch(
          apiSuccess(
            "GET",
            appointmentsKey,
            "Fetched appointments successfully",
          ),
        );
      } catch (error) {
        console.error("Error fetching appointments:", error);
        setError(error.message || "Failed to fetch appointments");
        dispatch(apiFetchError("GET", appointmentsKey, error.message));
      }
    };

    if (id) {
      fetchAppointments();
    }
  }, [id, dispatch, appointmentsKey]);

 

  /**
   * Badge class mappings
   * @returns Badge class object
   */
  const statusBadgeClass = (darkMode, status) => {
    const defaultClasses = {
      scheduled: darkMode
        ? "bg-blue-800 !text-blue-100 border border-blue-700"
        : "bg-blue-100 !text-blue-800 border border-blue-300",
      "in-progress": darkMode
        ? "bg-violet-800 !text-violet-100 border border-violet-700"
        : "bg-violet-100 !text-violet-800 border border-violet-300",
      completed: darkMode
        ? "bg-emerald-800 !text-emerald-100 border border-emerald-700"
        : "bg-emerald-100 !text-emerald-800 border border-emerald-300",
      cancelled: darkMode
        ? "bg-rose-800 !text-rose-100 border border-rose-700"
        : "bg-rose-100 !text-rose-800 border border-rose-300",
      "no-show": darkMode
        ? "bg-amber-800 !text-amber-100 border border-amber-700"
        : "bg-amber-100 !text-amber-800 border border-amber-300",
      confirmed: darkMode
        ? "bg-yellow-800 !text-yellow-100 border border-yellow-700"
        : "bg-yellow-100 !text-yellow-800 border border-yellow-300",
    };
    return (
      defaultClasses[status] ||
      (darkMode
        ? "bg-slate-800 !text-slate-100 border border-slate-700"
        : "bg-slate-100 !text-slate-800 border border-slate-300")
    );
  };

  const handleViewAll = () => {
    navigate("/appointments");
  };

  // Show placeholder while loading
  if (patientLoading) {
    return <UpcomingAppointmentsPlaceholder darkMode={darkMode} />;
  }

  // Show empty state if no appointments
  if (!appointments || appointments.length === 0) {
    return (
      <div
        className={arClass(`p-5 sm:rounded-2xl sm:shadow-[0_1px_4px_rgba(0,0,0,0.06)] sm:border sm:border-slate-100 overflow-hidden 
                      bg-transparent shadow-none border-0
                      ${darkMode ? "sm:bg-gray-800 sm:border-gray-700" : "sm:bg-white sm:border-slate-100"}`)}
      >
        <h3
          className={arClass(
            `mb-3.5 text-[13px] font-bold ${darkMode ? "text-gray-100" : "text-gray-900"}`,
          )}
        >
          <Text>Upcoming Appointments</Text>
        </h3>
        <div
          className={arClass("flex flex-col items-center justify-center py-8")}
        >
          <Calendar
            className={arClass(
              `w-12 h-12 ${darkMode ? "text-gray-600" : "text-gray-300"} mb-3`,
            )}
          />
          <p
            className={arClass(
              `text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`,
            )}
          >
            <Text>No upcoming appointments</Text>
          </p>
        </div>
      </div>
    );
  }

  const extractMonth = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(defaultLanguage, { month: "short" }).toUpperCase();
  }
  const extractDay = (dateString) => {
    const date = new Date(dateString);
    return date.getDate().toString().padStart(2, "0");
  }


  return (
    <div
      className={arClass(`p-5 sm:rounded-2xl sm:shadow-[0_1px_4px_rgba(0,0,0,0.06)] sm:border sm:border-slate-100 overflow-hidden}
                    bg-transparent shadow-none border-0
                    ${darkMode ? "sm:bg-gray-800 sm:border-gray-700" : "sm:bg-white sm:border-slate-100"}`)}
    >
      <h3
        className={arClass(
          `mb-3.5 text-[13px] font-bold ${darkMode ? "text-gray-100" : "text-gray-900"}`,
        )}
      >
        <Text>Upcoming Appointments</Text>
      </h3>

      <div className="flex flex-col">
        {appointments.map((a, i) => (
          <div
            key={i}
            className={arClass(
              `flex items-center justify-between py-2.5 ${i < appointments.length - 1 ? `border-b ${darkMode ? "border-gray-700" : "border-slate-50"}` : ""}`,
            )}
          >
            <div className={arClass("flex items-center gap-2.5")}>
              <div
                className={arClass(
                  `w-11 shrink-0 rounded-lg p-1 text-center ${darkMode ? "bg-blue-900/30" : "bg-blue-50"}`,
                )}
              >
                <p
                  className={arClass(
                    `text-[9px] font-bold uppercase tracking-wide ${darkMode ? "text-blue-400" : "text-blue-500"}`,
                  )}
                >
                  {extractMonth(a.appointmentDate)}
                </p>
                <p
                  className={arClass(
                    `text-[18px] font-extrabold leading-[1.1] ${darkMode ? "text-blue-400" : "text-blue-600"}`,
                  )}
                >
                  {extractDay(a.appointmentDate)}
                </p>
              </div>
              <div>
                <p
                  className={arClass(
                    `text-[13px] font-semibold ${darkMode ? "text-gray-100" : "text-gray-900"}`,
                  )}
                >
                  {typeTranslator(a.serviceType.nameEn).translatedType}
                </p>
                <p
                  className={arClass(
                    `text-[11px] ${darkMode ? "text-gray-500" : "text-gray-400"}`,
                  )}
                >
                  {formatTime(a.appointmentTime) }
                </p>
              </div>
            </div>

            <div className={arClass("flex items-center gap-1.5")}>
              <span
                className={arClass(
                  `rounded-lg px-2 py-0.5 text-[10px] font-semibold ${statusBadgeClass(darkMode, a.status)}`,
                )}
              >
                <Text>{a.status}</Text>
              </span>
              {defaultLanguage === "ar" ? (
                <ChevronLeft className={arClass("w-3 h-3")} />
              ) : (
                <ChevronRight className={arClass("w-3 h-3")} />
              )}
            </div>
          </div>
        ))}
      </div>

      <button
        className={arClass(
          `mt-3.5 flex items-center gap-1 text-[12px] font-medium text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors`,
        )}
        onClick={handleViewAll}
      >
        <Text>View all appointments</Text>
        {defaultLanguage === "ar" ? (
          <ChevronLeft className={arClass("w-3 h-3")} />
        ) : (
          <ChevronRight className={arClass("w-3 h-3")} />
        )}
      </button>
    </div>
  );
};

export default UpcomingAppointments;
